/**
 * /accept-invitation?code=XXXX-XXXX-XXXX
 *
 * Landing page from an emailed invite link. Validates the code via the
 * (non-consuming) /invites/validate endpoint, then either:
 *   - prompts unauthenticated users to sign in / sign up (returning here
 *     after auth), or
 *   - lets authenticated users redeem the invite via /invites/redeem.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { post } from '@/shared/api/client';
import { Button } from '@/interfaces/learner/ui';
import LoadingIcon from '@/foundation/ui/LoadingIcon';
import { useAuth } from '@/shared/auth/AuthProvider';
import AuthLayout from '@/shared/auth/AuthLayout';
import Logomark from '@/shared/components/Logomark';

interface ValidateResponse {
  invite: { id: string; rewardType: string | null };
}

interface RedeemResponse {
  invite: { id: string; rewardType: string | null; rewardValue: string | null };
}

type Status = 'validating' | 'invalid' | 'valid' | 'redeeming' | 'redeemed';

// Header is defined module-level (not inside the component) so React doesn't
// recreate the component on every render — see eslint-plugin-react-hooks
// `static-components` rule.
function Header({ title }: { title: string }) {
  return (
    <div className="text-center mb-8">
      <div className="mb-4 flex justify-center">
        <Logomark size={48} />
      </div>
      <h1 className="text-2xl font-bold">{title}</h1>
    </div>
  );
}

export default function AcceptInvitationPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code') ?? '';
  const { isAuthed, loading } = useAuth();

  const [status, setStatus] = useState<Status>('validating');
  const [rewardType, setRewardType] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string>('');

  useEffect(() => {
    if (!code) {
      setStatus('invalid');
      setErrorMsg('Missing invite code');
      return;
    }
    // Validating an invite while unauthenticated will return 401 from the
    // route's authorize() guard. Defer the validate call until the user is
    // authed — the unauthenticated UX just shows the "sign in to accept"
    // CTA without revealing whether the code is valid.
    if (loading || !isAuthed) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await post<ValidateResponse>('/invites/validate', { code });
        if (cancelled) return;
        setStatus('valid');
        setRewardType(data.invite.rewardType);
      } catch (e: unknown) {
        if (cancelled) return;
        setStatus('invalid');
        setErrorMsg((e as Error).message ?? 'Invalid invite');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [code, loading, isAuthed]);

  const onRedeem = async () => {
    setStatus('redeeming');
    try {
      await post<RedeemResponse>('/invites/redeem', { code });
      setStatus('redeemed');
      toast.success('Invite redeemed');
      setTimeout(() => navigate('/', { replace: true }), 800);
    } catch (e: unknown) {
      setStatus('valid');
      toast.error((e as Error).message ?? 'Could not redeem');
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <AuthLayout>
        <Header title="One moment…" />
        <div className="flex justify-center">
          <LoadingIcon size="md" />
        </div>
      </AuthLayout>
    );
  }

  if (!isAuthed) {
    // Encode the FULL inner URL so the inner `?code=...` doesn't get
    // truncated by the outer query parser when it lands on /sign-up.
    const inner = encodeURIComponent(`/accept-invitation?code=${code}`);
    // Invite recipients are new users by design — sign-up is the
    // primary action, not a peer of sign-in. The rare case of someone
    // who already has an account (forwarded invite, etc.) gets a small
    // text link below, not an equal CTA.
    return (
      <AuthLayout>
        <Header title="Accept your invite" />
        <div className="space-y-4 text-center">
          <p className="text-sm">Create your account to accept your invite.</p>
          <Button
            variant="primary"
            size="lg"
            onPress={() => navigate(`/sign-up?redirect=${inner}`)}
            className="w-full"
          >
            Create account
          </Button>
          <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
            Already have an account?{' '}
            <button
              type="button"
              onClick={() => navigate(`/login?redirect=${inner}`)}
              className="hover:underline"
              style={{ color: 'var(--color-accent-text)' }}
            >
              Sign in
            </button>
          </p>
        </div>
      </AuthLayout>
    );
  }

  if (status === 'validating') {
    return (
      <AuthLayout>
        <Header title="Validating invite" />
        <div className="flex justify-center">
          <LoadingIcon size="md" />
        </div>
      </AuthLayout>
    );
  }

  if (status === 'invalid') {
    return (
      <AuthLayout>
        <Header title="Invalid invite" />
        <div className="space-y-4 text-center">
          <p className="text-sm">{errorMsg}</p>
          <Link
            to="/"
            className="text-sm hover:underline"
            style={{ color: 'var(--color-accent-text)' }}
          >
            Go to dashboard
          </Link>
        </div>
      </AuthLayout>
    );
  }

  if (status === 'redeemed') {
    return (
      <AuthLayout>
        <Header title="Welcome to Formency" />
        <p className="text-sm text-center">Redirecting…</p>
      </AuthLayout>
    );
  }

  // status === 'valid' || 'redeeming'
  return (
    <AuthLayout>
      <Header title="Accept your invite" />
      <div className="space-y-4 text-center">
        {rewardType && (
          <p className="text-sm">
            This invite includes a reward — you&apos;ll receive it on redemption.
          </p>
        )}
        <Button
          variant="primary"
          size="lg"
          isDisabled={status === 'redeeming'}
          onPress={onRedeem}
          className="w-full"
        >
          {status === 'redeeming' ? (
            <span className="flex items-center justify-center gap-2">
              <LoadingIcon size="sm" />
              Accepting...
            </span>
          ) : (
            'Accept invite'
          )}
        </Button>
      </div>
    </AuthLayout>
  );
}
